/**
 * The type scale — six text sizes for the whole app, plus free-form hero
 * numbers.
 *
 * Before September 2026 the codebase used 44 distinct `fontSize` values
 * (0.4rem to 2.8rem) across ~1,300 places, most of them a designer's guess
 * one notch away from a neighbour: 0.65 next to 0.7, 0.75 next to 0.78 next
 * to 0.8. Nothing looked wrong on its own and nothing looked like one hand
 * drew it. Every text size in `sx` was remapped to the nearest step below;
 * only hero numbers (1.5rem and up — the big score, gauge values) keep their
 * own sizes because each one is composed against its card.
 *
 * Use these in new code. Sizes under `micro` are not allowed: the smallest
 * text on a phone is 0.6rem (9.6px), which is already the floor for a
 * legible mono caption; the old 0.4–0.5rem footnotes were unreadable by
 * design and have been raised.
 *
 * Remember the phone trap: `Typography` with a `variant` gets a forced size
 * from global.css on small screens. For a deliberately sized label use `Txt`
 * (design-system) or a `Box`, and these tokens.
 */
export const FONT = {
  /** 0.6rem — tile captions, chart tick labels, footnotes. The floor. */
  micro: '0.6rem',
  /** 0.7rem — captions, chips, mono section labels, table cells. */
  caption: '0.7rem',
  /** 0.8rem — secondary body text, card subtitles, list rows. */
  small: '0.8rem',
  /** 0.9rem — body text, inputs, dialog copy. */
  body: '0.9rem',
  /** 1rem — lead text, card titles, headline sentences. */
  lead: '1rem',
  /** 1.25rem — page and section titles, dialog titles. */
  title: '1.25rem',
} as const;

export type FontStep = keyof typeof FONT;

/**
 * Snap an arbitrary rem size to the scale. Used by the one-off remap script
 * and handy when porting an old component: `snapFontSize(0.78) === '0.8rem'`.
 * Sizes of 1.5rem and above are hero numbers and are returned unchanged.
 */
export function snapFontSize(rem: number): string {
  if (rem >= 1.5) return `${rem}rem`;
  if (rem <= 0.62) return FONT.micro;
  if (rem <= 0.72) return FONT.caption;
  if (rem <= 0.82) return FONT.small;
  if (rem <= 0.96) return FONT.body;
  if (rem <= 1.12) return FONT.lead;
  return FONT.title;
}
